import { useTranslation } from 'react-i18next';
import * as stylex from '@stylexjs/stylex';
import { Button } from '@sentinel/ui';
import type { SessionItem } from '../use-sessions';
import { authMethodLabel } from '../auth-method-label';
import { parseUserAgent } from '../../../lib/parse-user-agent';
import { sentinel } from '@sentinel/ui/theme/semantic.stylex';

const styles = stylex.create({
  row: {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    alignItems: 'center',
    gap: '0.75rem',
    paddingTop: '0.75rem',
    paddingBottom: '0.75rem',
    paddingLeft: '1rem',
    paddingRight: '1rem',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: sentinel.borderDefault,
    borderRadius: '0.5rem',
    backgroundColor: `color-mix(in oklab, ${sentinel.surfaceElevated} 60%, transparent)`,
  },
  title: { fontWeight: 600, display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' },
  meta: {
    fontSize: '0.8125rem',
    color: sentinel.textMuted,
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.25rem 1rem',
    marginTop: '0.25rem',
  },
  badge: {
    display: 'inline-block',
    paddingTop: '0.125rem',
    paddingBottom: '0.125rem',
    paddingLeft: '0.5rem',
    paddingRight: '0.5rem',
    borderRadius: '999px',
    fontSize: '0.75rem',
    backgroundColor: `color-mix(in oklab, ${sentinel.brandPrimary} 20%, transparent)`,
    color: 'oklch(82% 0.16 353.56)',
  },
  // Login-method chip: deliberately quieter than the pink "This device" badge.
  // An outlined ghost pill (hairline border, near-transparent fill) reads as
  // secondary metadata and sits comfortably in the muted meta row instead of
  // competing with the filled accent badge.
  methodBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.375rem',
    paddingTop: '0.0625rem',
    paddingBottom: '0.0625rem',
    paddingLeft: '0.5rem',
    paddingRight: '0.625rem',
    borderRadius: '999px',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: `color-mix(in oklab, ${sentinel.surfaceInverse} 14%, transparent)`,
    backgroundColor: `color-mix(in oklab, ${sentinel.surfaceInverse} 4%, transparent)`,
    fontSize: '0.6875rem',
    fontWeight: 500,
    letterSpacing: '0.02em',
    color: `color-mix(in oklab, ${sentinel.surfaceInverse} 72%, transparent)`,
    lineHeight: 1.4,
  },
  // Small leading dot that anchors the chip as a labeled tag.
  methodDot: {
    width: '0.3125rem',
    height: '0.3125rem',
    borderRadius: '999px',
    backgroundColor: `color-mix(in oklab, ${sentinel.surfaceInverse} 50%, transparent)`,
    flexShrink: 0,
  },
});


type TFn = ReturnType<typeof useTranslation<'sessions'>>['t'];

function relativeTime(iso: string, t: TFn): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const diff = Date.now() - then;
  const seconds = Math.max(0, Math.round(diff / 1000));
  if (seconds < 60) return t('last_activity_just_now');
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return t('last_activity_minutes_ago', { count: minutes });
  const hours = Math.round(minutes / 60);
  if (hours < 24) return t('last_activity_hours_ago', { count: hours });
  const days = Math.round(hours / 24);
  return t('last_activity_days_ago', { count: days });
}

export interface SessionRowProps {
  session: SessionItem;
  isCurrent: boolean;
  revoking: boolean;
  onRevoke: (s: SessionItem) => void;
}

export function SessionRow({ session, isCurrent, revoking, onRevoke }: SessionRowProps) {
  const { t } = useTranslation('sessions');
  const device = parseUserAgent(session.device_info.user_agent, t('device_unknown'));
  const ip = session.device_info.ip_address?.trim() || t('location_unknown');
  const lastActive = relativeTime(session.last_active_at, t);
  const methodLabel = authMethodLabel(session.login_method, t);

  return (
    <li
      {...stylex.props(styles.row)}
      data-testid={`session-row-${session.id}`}
      data-current={isCurrent ? 'true' : 'false'}
    >
      <div>
        <div {...stylex.props(styles.title)}>
          <span>{device}</span>
          {isCurrent ? (
            <span {...stylex.props(styles.badge)} data-testid="session-this-device-badge">
              {t('this_device')}
            </span>
          ) : null}
        </div>
        <div {...stylex.props(styles.meta)}>
          <span>{ip}</span>
          <span>{lastActive}</span>
          {session.login_method ? (
            <span
              {...stylex.props(styles.methodBadge)}
              data-testid={`session-login-method-${session.id}`}
            >
              <span {...stylex.props(styles.methodDot)} aria-hidden="true" />
              {methodLabel}
            </span>
          ) : null}
        </div>
      </div>
      <Button
        type="button"
        variant={isCurrent ? 'secondary' : 'danger'}
        size="sm"
        onClick={() => onRevoke(session)}
        disabled={revoking}
        data-testid={`session-revoke-${session.id}`}
      >
        {isCurrent ? t('revoke_current_button') : t('revoke_button')}
      </Button>
    </li>
  );
}
